import * as React from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { CardContent, CardHeader, Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { JobRequest, JobRequestFileCategory, LinguisticQualityReviewPhase } from "gen/clients";
import { SelectInput } from "components/SelectInput/SelectInput";
import { SelectOption } from "@mui/base";
import { SELECT_OPTION_COMPARATOR } from "utils/stringUtils";
import { FileList } from "../../components/FileList/FileList";
import { LinguisticQualityReview } from "./LinguisticQualityReview";

interface Props {
  jobRequest: JobRequest;
}

const FilesSection: React.FC<Props> = ({ jobRequest }) => {
  const [selectedTargetLanguageId, setSelectedTargetLanguageId] = React.useState<string>();

  const targetLanguageOptions: SelectOption<string>[] = React.useMemo(() => {
    // Check if there are any translated files with source language
    const hasSourceLanguageTranslatedFiles = jobRequest.files?.some(
      f => f.category === JobRequestFileCategory.TRANSLATED_DOCUMENT && f.languageId === jobRequest.sourceLanguage?.id
    );
    // Start with target languages
    const options =
      jobRequest.targetLanguages?.map(l => ({
        label: l.name,
        value: l.id as string
      })) || [];

    // Add source language if there are translated files with source language
    if (hasSourceLanguageTranslatedFiles && jobRequest.sourceLanguage) {
      options.push({
        label: `${jobRequest.sourceLanguage.name} (Source)`,
        value: jobRequest.sourceLanguage.id as string
      });
    }

    // Sort the options
    return options.sort(SELECT_OPTION_COMPARATOR);
  }, [jobRequest.files, jobRequest.targetLanguages, jobRequest.sourceLanguage]);

  React.useEffect(() => {
    if (targetLanguageOptions && targetLanguageOptions.length) {
      setSelectedTargetLanguageId(targetLanguageOptions[0].value);
    }
  }, [targetLanguageOptions]);

  const sourceFiles = React.useMemo(
    () => jobRequest.files?.filter(f => f.category === JobRequestFileCategory.SOURCE_DOCUMENT) || [],
    [jobRequest.files]
  );

  const otherFiles = React.useMemo(
    () => jobRequest.files?.filter(f => f.category === JobRequestFileCategory.OTHER) || [],
    [jobRequest.files]
  );

  const translatedFiles = React.useMemo(() => {
    if (!selectedTargetLanguageId) {
      return [];
    }

    return (
      jobRequest.files?.filter(
        f => f.languageId === selectedTargetLanguageId && f.category === JobRequestFileCategory.TRANSLATED_DOCUMENT
      ) || []
    );
  }, [selectedTargetLanguageId, jobRequest]);

  const linguisticQualityReview = React.useMemo(() => {
    if (!selectedTargetLanguageId || !jobRequest.linguisticQualityReviewResults) {
      return null;
    }

    return jobRequest.linguisticQualityReviewResults.find(
      result =>
        result.languageId === selectedTargetLanguageId && result.phase === LinguisticQualityReviewPhase.AI_REVIEW
    );
  }, [selectedTargetLanguageId, jobRequest.linguisticQualityReviewResults]);

  const selectedLanguageName = React.useMemo(() => {
    if (!selectedTargetLanguageId) {
      return null;
    }

    // Check if it's the source language
    if (selectedTargetLanguageId === jobRequest.sourceLanguage?.id) {
      return jobRequest.sourceLanguage.name;
    }

    // Find in target languages
    const targetLanguage = jobRequest.targetLanguages?.find(lang => lang.id === selectedTargetLanguageId);
    return targetLanguage?.name || null;
  }, [selectedTargetLanguageId, jobRequest.sourceLanguage, jobRequest.targetLanguages]);

  return (
    <Grid container={true} spacing={2}>
      <Grid item={true} xs={12} sm={6}>
        <Card sx={{ mt: 2, height: 400 }}>
          <CardHeader
            title={
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h6">Source Files</Typography>
                <Box sx={{ height: 40, paddingTop: "5px" }}>
                  <Typography variant="body1">{jobRequest.sourceLanguage?.name}</Typography>
                </Box>
              </Stack>
            }
          />
          <CardContent sx={{ overflowY: "auto", pt: 0 }}>
            <FileList jobRequestId={jobRequest.id} files={sourceFiles} />

            {otherFiles.length > 0 && (
              <Stack mt={3} spacing={1}>
                <Typography fontWeight="bold">Other Files</Typography>
                <FileList jobRequestId={jobRequest.id} files={otherFiles} />
              </Stack>
            )}
          </CardContent>
        </Card>
      </Grid>
      <Grid item={true} xs={12} sm={6}>
        <Card sx={{ mt: 2, height: 400, display: "flex", flexDirection: "column" }}>
          <CardHeader
            title={
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h6">Deliverable Files</Typography>
                <SelectInput
                  options={targetLanguageOptions}
                  onChange={setSelectedTargetLanguageId}
                  value={selectedTargetLanguageId}
                  size="small"
                  SelectProps={{
                    native: true
                  }}
                />
              </Stack>
            }
          />
          <CardContent sx={{ overflowY: "auto", pt: 0, flex: 1, display: "flex", flexDirection: "column" }}>
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {translatedFiles.length === 0 && <Typography variant="body2">No files</Typography>}
              {translatedFiles.length > 0 && <FileList jobRequestId={jobRequest.id} files={translatedFiles} />}
            </Box>

            {linguisticQualityReview && selectedLanguageName && (
              <LinguisticQualityReview reviewResult={linguisticQualityReview} languageName={selectedLanguageName} />
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export { FilesSection };
